import type { RoomMember } from "@core/features/videoMeeting";

/**
 * Extract display name and room member data from Chime externalUserId.
 * Format: firstName-lastName-TS-{timestamp}-UID-{userId}
 *
 * Parsing is done from the end to handle names that contain
 * "-TS-" or "-UID-" as substrings (e.g. "Dr-TS-Smith"). We find the
 * last occurrence of each delimiter to ensure correct extraction.
 */
export function extractRoomMemberData(
  attendee: { externalUserId?: string; name?: string } | null | undefined,
  roomMembers?: Record<number, RoomMember>
): { displayName: string; roomMemberData: RoomMember | null } {
  let displayName = "";
  let roomMemberData: RoomMember | null = null;

  if (attendee?.externalUserId) {
    const id = attendee.externalUserId;
    const uidIdx = id.lastIndexOf("-UID-");

    if (uidIdx !== -1) {
      const userId = parseInt(id.slice(uidIdx + 5), 10);
      const beforeUid = id.slice(0, uidIdx);
      const tsIdx = beforeUid.lastIndexOf("-TS-");

      if (tsIdx !== -1) {
        const namePart = beforeUid.slice(0, tsIdx);
        displayName = namePart.replace(/-/g, " ");
      } else {
        displayName = beforeUid.replace(/-/g, " ");
      }

      if (roomMembers && !isNaN(userId)) {
        roomMemberData = roomMembers[userId] || null;
      }
    } else {
      // Fallback: no -UID- found, try legacy -TS- split
      const tsIdx = id.lastIndexOf("-TS-");
      if (tsIdx !== -1) {
        displayName = id.slice(0, tsIdx).replace(/-/g, " ");
      } else {
        displayName = id.replace(/-/g, " ");
      }
    }
  } else {
    displayName = attendee?.name || "";
  }

  return { displayName, roomMemberData };
}
